/**
 * Stored JSON, presented rather than printed.
 *
 * The console kept reaching for `JSON.stringify` and a `truncate` class, which
 * produced things like `{"run_id":"52092be4-02da-48a0-a902-1020a6600191","garm…`
 * and `["garment_classify","garment_digitise","garment_extrac…` - cut off
 * mid-key and mid-word, unreadable, and impossible to select as data. A stored
 * object is key/value rows; a stored list is a list. Nothing is ever cut in
 * the middle of a token: a long value collapses to a count and opens in full,
 * as selectable text.
 *
 * The precise identifier an operator will grep for always leads; where one
 * needs explaining, `Term` from `./glossary` carries it, so there is one
 * explanation layer rather than two.
 */

import { useState } from 'react';
import { humanize } from './format';
import { Code } from './primitives';

/** Anything longer than this is collapsed rather than cut. */
const INLINE_LIMIT = 120;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function scalarText(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value) ?? String(value);
}

/**
 * A value too long to sit in a row. It reports what it is and how much of it
 * there is, and the whole thing is one click away and selectable - never a
 * prefix with an ellipsis glued to it.
 */
function Expandable({ label, full }: { label: string; full: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="text-xs font-medium text-brand-secondary underline outline-brand focus-visible:outline-2"
      >
        {open ? `Hide ${label}` : `Show ${label}`}
      </button>
      {open ? (
        <pre className="max-h-64 w-full overflow-auto rounded-lg bg-secondary p-3 font-mono text-xs break-words whitespace-pre-wrap text-secondary select-text">
          {full}
        </pre>
      ) : null}
    </span>
  );
}

/** A list of short values, as chips rather than as a stringified array. */
export function ValueChips({ values }: { values: readonly unknown[] }) {
  if (!values.length) return <span className="text-sm text-quaternary">Empty list</span>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {values.map((value, index) => (
        <li key={`${scalarText(value)}-${index}`}>
          <span className="inline-block rounded-md bg-secondary px-2 py-0.5 font-mono text-xs text-secondary select-text">
            {scalarText(value)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Render a stored value as what it is.
 *
 * - an object becomes key/value rows, recursing one level
 * - a list of scalars becomes chips
 * - a list of objects, or anything deeper, collapses to a labelled disclosure
 * - a scalar is text, and a long one collapses rather than being cut
 */
export function StructuredValue({
  value,
  depth = 0,
}: {
  value: unknown;
  /** Guards against rendering an arbitrarily deep payload as nested rows. */
  depth?: number;
}) {
  if (value === null || value === undefined)
    return <span className="text-sm text-quaternary">Not recorded</span>;

  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-sm text-quaternary">Empty list</span>;
    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item)))
      return <ValueChips values={value} />;
    return (
      <Expandable
        label={`${value.length} ${value.length === 1 ? 'entry' : 'entries'}`}
        full={JSON.stringify(value, null, 2)}
      />
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (!entries.length) return <span className="text-sm text-secondary">Recorded, and empty</span>;
    if (depth >= 1)
      return (
        <Expandable
          label={`${entries.length} ${entries.length === 1 ? 'field' : 'fields'}`}
          full={JSON.stringify(value, null, 2)}
        />
      );
    return (
      <dl className="m-0 flex min-w-0 flex-col gap-1.5">
        {entries.map(([key, entry]) => (
          <div key={key} className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="shrink-0 text-xs text-tertiary sm:w-40">{humanize(key)}</dt>
            <dd className="m-0 min-w-0 flex-1">
              <StructuredValue value={entry} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  const text = scalarText(value);
  if (text.length > INLINE_LIMIT)
    return <Expandable label={`${text.length} characters`} full={text} />;
  return (
    <span className="font-mono text-xs break-all text-secondary select-text">
      <Code>{text}</Code>
    </span>
  );
}
